import React from 'react';
import {Form, Header, Grid, Icon, Segment} from 'semantic-ui-react';
import _ from 'lodash';
import pluralize from 'pluralize';
import {safeMult, safeAdd, showDollarAmountWithDollar} from 'common/utils';
import {REQUEST_DETAILS_VALUE_CHANGE_DISPLAY} from 'app-ui-componentlibrary/dist/common/constants';
import ReadOnlyDollarSignField from 'app-ui-componentlibrary/dist/go/components/common/ReadOnlyDollarSignField';
import {InlineSelect} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import LineItem from 'go/components/modules/requestDetails/v3/items/shared/LineItem';
import {BOCS} from 'app-ui-componentlibrary/dist/common/constants';

import {toItemCode} from 'common/requestDetails/utils';
import {QUANTITY, DESCRIPTION, ANNUAL_BENEFITS, ADDITIONAL_INFORMATION} from 'common/requestDetails/constants.js';
import {ExpandingTextArea} from 'go/components/modules/requestDetails/v3/items/shared';
import StaffingBundledPayment from 'go/components/modules/requestDetails/v3/items/paymentRequest/staffingBundled/StaffingBundledPayment';

// NOTE: Staffing Bundled Internal code is not currently in use and not in any requirements, leftover from V2 version

const StaffingBundledInternalData = (props) => {
  const {fullTimePositionLabel, id, costs, period} = props;

  const ANNUAL_SALARY_PATH = 'annualSalary.price';
  const ANNUAL_BENEFITS_PATH = 'annualBenefits.price';
  const numberPositionsLabel = `Number of ${pluralize(
    fullTimePositionLabel || 'position'
  )}`;
  const annualBenefitsLabel = REQUEST_DETAILS_VALUE_CHANGE_DISPLAY[ANNUAL_BENEFITS];
  const annualStaffingCosts = _.get(costs, 0);
  const findIndex = (boc) => _.findIndex(annualStaffingCosts, (cost) => cost.boc === boc);
  const annualSalaryIndex = findIndex(BOCS.PERSONNEL);
  const annualBenefitsIndex = findIndex(BOCS.FRINGE_BENEFITS);
  const annualSalary = annualStaffingCosts[annualSalaryIndex] || {};
  const annualBenefits = annualStaffingCosts[annualBenefitsIndex] || {};
  const staffing = {
    annualSalary,
    annualBenefits,
    [fullTimePositionLabel]: _.get(annualSalary, QUANTITY),
  };
  const annualSalaryPrice = _.get(staffing, ANNUAL_SALARY_PATH);
  const annualBenefitsPrice = _.get(staffing, ANNUAL_BENEFITS_PATH);
  const numberPositions = _.get(staffing, fullTimePositionLabel);

  return (
    <div>
      <Grid stackable>
        <Grid.Row className="cost-row">
          <Grid.Column mobile={8} computer={4}>
            <Header as="h6">{numberPositionsLabel}</Header>
            <p>{numberPositions}</p>
          </Grid.Column>
          <Grid.Column mobile={8} computer={4}>
            <ReadOnlyDollarSignField
              value={annualSalaryPrice}
              alignLeft
              defaultValue=""
            />
          </Grid.Column>
          <Grid.Column mobile={8} computer={4}>
            <Header as="h6">{annualBenefitsLabel}</Header>
            <ReadOnlyDollarSignField
              value={annualBenefitsPrice}
              alignLeft
              defaultValue=""
            />
          </Grid.Column>
          <Grid.Column mobile={8} computer={4}>
            <Header as="h6">Total per firefighter</Header>
            <ReadOnlyDollarSignField
              value={safeAdd(annualSalaryPrice, annualBenefitsPrice)}
              alignLeft
              defaultValue=""
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column mobile={8} computer={4}>
            <Header
              as="h6"
              className="inline"
              id={`${period.increments}-${period.increments}-total-${id}`}
            >
              {period.increments} {period.type} total
            </Header>
          </Grid.Column>
          <Grid.Column mobile={8} computer={4}>
            <strong aria-label="3 Year total">
              {showDollarAmountWithDollar(
                safeMult(
                  numberPositions,
                  safeAdd(annualSalaryPrice, annualBenefitsPrice),
                  period.increments
                )
              )}
            </strong>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export const StaffingBundledInternal = (props) => {
  const {
    item: lineItem,
    itemCodeOptions,
    editable,
    id,
    handleBlur,
    handleChange,
    beingCreated,
    parseError,
    itemConfig,
    period,
    openDeleteModal,
    deletable,
    validationErrors,
    validationDirtyMap,
  } = props;
  const FULLTIME_POSITION = _.get(itemConfig, 'labels.position');
  const descriptionLabel = _.get(
    itemConfig,
    'labels.description',
    REQUEST_DETAILS_VALUE_CHANGE_DISPLAY[DESCRIPTION]
  );
  const descriptionPath = 'additionalInformation.' + DESCRIPTION;

  const ITEM_COST_CODE = 'itemCode';
  const itemCostCodeLabel = _.upperFirst(
    _.get(
      itemConfig,
      'labels.itemLabel',
      REQUEST_DETAILS_VALUE_CHANGE_DISPLAY[ITEM_COST_CODE]
    )
  );
  const itemCostCodePath = ITEM_COST_CODE;
  const itemCost = _.get(lineItem, 'itemCode');

  const description = _.get(lineItem, descriptionPath);
  const itemName = _.get(itemConfig, 'name', '');
  const itemTitle = itemName;

  const handleItemChange = (name, value) => {
    let newLineItem = _.cloneDeep(lineItem);
    _.set(newLineItem, name, value);
    return handleChange(newLineItem);
  };

  const handleItemChangeAndBlur = (name, value, fieldName) => {
    handleItemChange(name, value).then((res) => {
      handleBlur(fieldName);
    });
  };

  const displayError = (path, readOnly) => {
    const showError = !_.get(validationErrors, `${path}.isValid`, true);
    return readOnly ? showError : showError && _.get(validationDirtyMap, path);
  };

  const errorMessages = {
    [descriptionPath]: `Missing ${descriptionLabel}`,
  };

  return (
    <React.Fragment>
      <Grid.Column width={5}>
        <div className="section-1">
          {beingCreated && !_.isEmpty(itemCodeOptions) ? (
            <InlineSelect
              options={itemCodeOptions}
              label={itemCostCodeLabel}
              name={itemCostCodePath}
              handleChange={(e) =>
                handleItemChangeAndBlur(
                  ITEM_COST_CODE,
                  toItemCode(e.target.value),
                  ITEM_COST_CODE
                )
              }
              value={itemCost}
            />
          ) : (
            <Header as="h5">{itemName}</Header>
          )}
          {!!editable && !beingCreated && (
            <div className="row--space-between">
              {deletable && (
                <a
                  id="delete-details"
                  href="#"
                  onClick={openDeleteModal}
                  title={`Open form to remove ${itemConfig.name}`}
                >
                  <Icon link name="close" color="red" />
                  Remove
                </a>
              )}
            </div>
          )}
        </div>
      </Grid.Column>
      <Grid.Column width={11}>
        <Grid.Row>
          <ExpandingTextArea
            id={id}
            value={description}
            label={descriptionLabel}
            closedLabel={descriptionLabel}
            fieldName={DESCRIPTION}
            expanded={false}
            handleChange={(value) => handleItemChange(descriptionPath, value)}
            handleBlur={() => handleBlur(descriptionPath)}
            errorMessages={errorMessages[descriptionPath]}
            displayError={displayError(descriptionPath)}
          />
        </Grid.Row>
        <Grid.Row className="padding-top--double">
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <StaffingBundledInternalData
                  id={id}
                  parseError={parseError}
                  costs={_.get(lineItem, 'cost')}
                  fullTimePositionLabel={FULLTIME_POSITION}
                  handleChange={handleItemChange}
                  handleBlur={handleBlur}
                  period={period}
                  validationErrors={_.get(validationErrors, 'cost')}
                  validationDirtyMap={_.get(validationDirtyMap, 'cost')}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Row>
      </Grid.Column>
    </React.Fragment>
  );
};

export default class StaffingBundled extends React.Component {
  state = {
    lineItem: this.props.item,
  };

  handleChange = (lineItem) => {
    return new Promise((resolve) => {
      this.setState({lineItem}, () => resolve());
    });
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;

      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  render() {
    const {lineItem} = this.state;
    return (
      <LineItem 
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        lineItem={lineItem}
        canEdit={this.props.editable}
        {...this.props}>
        {(p) => {
          const label = `Line Item: ${p.itemConfig.name}`;
          const className = `request-details-card no-padding ${
            'request-details--v2__tier' + p.tier
          }`;
          return (
            <React.Fragment>
              {this.props.showInternal ? (
                <Segment className={className} aria-label={label} id={p.id}>
                  <Form>
                    <Grid
                      celled="internally"
                      className="request-details-card__main util--full-width"
                    >
                      <StaffingBundledInternal
                        item={p.item}
                        handleBlur={p.handleBlur}
                        handleChange={p.handleChange}
                        deletable={p.deletable}
                        isNewlyCreated={p.isNewlyCreated}
                        editable={p.editable}
                        beingCreated={p.beingCreated}
                        id={p.id}
                        itemConfig={p.itemConfig}
                        itemCodeOptions={p.itemCodeOptions}
                        period={p.period}
                        pastTotalPayment={p.pastTotalPayment}
                        openDeleteModal={p.openDeleteModal}
                        closeDeleteModal={p.closeDeleteModal}
                      />
                    </Grid>
                  </Form>
                </Segment>
                ) : (
                  ''
                )}
              <StaffingBundledPayment
                item={p.item}
                roster={p.roster}
                editable={p.editable}
                handleBlur={p.handleBlur}
                hasPayment={p.hasPayment}
                handleChange={p.handleChange}
                validationErrors={p.validationErrors}
                validationDirtyMap={p.validationDirtyMap}
                paymentPeriod={p.paymentPeriod}
                fundingOpportunityStreams={p.fundingOpportunityStreams}
                staffingStreamType={p.staffingStreamType}
                periodOfPerformance={p.periodOfPerformance}
                approvedPayments={p.approvedPayments}
                totalsInfo={p.totalsInfo}
                grantBudgetShareFedRate={p.grantBudgetShareFedRate}
                turnOffvalidationLimits={p.turnOffvalidationLimits}
                tier={p.tier}
                itemConfig={p.itemConfig}
              />
            </React.Fragment>
          );
        }}
      </LineItem>
    );
  }
};
